import apiUseCases from "@src/cloud-connect/api";
import { FlatListProvider } from "@src/cloud-connect/node-manager/flat-list-provider";
import { saveEditStructurePageTransferData } from "@src/cloud-connect/node-manager/node-manager-repository";
import { updateEditStructurePageInitialData } from "@src/reducers/your-reducer";
import store from "@src/reducers/store";
import { OurNode } from "@src/cloud-connect/poc/our-node";
import { getInitialStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-initial-structure-nodeset";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { TransferredComparer } from "@src/cloud-connect/poc/transferred-comparer";
import { getLogger } from "@src/cloud-connect/dependency-injection-container/get-logger";
import { getTileCollection } from "../dependency-injection-container/get-tile-collection";
import { TileStructureParser } from "../blueprint/dashboards/entities/tile-structure-parser";

// module-level flag so components can correctly initialize their loaded state
// on remount without needing to wait for an observer that already fired
let _initialStructureLoadComplete = false;

// ============================================================================
// Helper Functions
// ============================================================================

export const hasInitialStructureLoadCompleted = () =>
  _initialStructureLoadComplete;

const getSourceInfo = () => {
  const source = store.getState().yourSlice.connectionView.source;
  const namespace = store.getState().yourSlice.authContext?.customerNamespace;
  const { source_id, auth_info, connected_via_client } = source;
  return { namespace, source_id, auth_info, connected_via_client };
};

const writeSavedMachineStructureNameIntoInitialStructure = (
  initialStructure: any,
  savedStructureWithKeys: any,
) => {
  // if initial structure nodeset is null, preserve it as null so the Redux dispatch
  // completes with valid data, which kills the spinner since loading is finished
  if (!initialStructure?.nodeset) {
    return initialStructure;
  }

  let savedMachineStructureName;
  if (savedStructureWithKeys.nodeset === null) {
    savedMachineStructureName = "";
  } else {
    savedMachineStructureName = savedStructureWithKeys.nodeset.name;
  }

  return {
    ...initialStructure,
    nodeset: {
      ...initialStructure.nodeset,
      name: savedMachineStructureName,
    },
  };
};

const writeInitialStructureNodesetRootNodeAsTreeInRedux = (
  rootNode: ChildNode,
): OurNode => {
  const ourNodeRootNodeTree = OurNode.makeObjectTree(rootNode);
  const initialStructureNodeset = getInitialStructureNodeset();
  initialStructureNodeset.setRootNode(ourNodeRootNodeTree);
  return ourNodeRootNodeTree;
};

const writeMachineStructureNodesetRootNodeAsTreeInRedux = (
  rootNode: ChildNode,
): OurNode => {
  const ourNodeRootNodeTree = OurNode.makeObjectTree(rootNode);
  const machineStructureNodeset = getMachineStructureNodeset();
  machineStructureNodeset.setRootNode(ourNodeRootNodeTree);
  return ourNodeRootNodeTree;
};

const writeInitialStructureNodesetRootNodeAsMapInRedux = (
  rootNode: OurNode,
) => {
  const newRootNodeMap = rootNode.createMapFromOurNodeTree();
  const initialStructureNodeset = getInitialStructureNodeset();
  initialStructureNodeset.setFlatMap(newRootNodeMap);
};

const writeMachineStructureNodesetRootNodeAsMapInRedux = (
  rootNode: OurNode,
) => {
  const newRootNodeMap = rootNode.createMapFromOurNodeTree();
  const machineStructureNodeset = getMachineStructureNodeset();
  machineStructureNodeset.setFlatMap(newRootNodeMap);
};

const setDashboardInfo = (dashboardInfoFromClient: any) => {
  const tileCollection = getTileCollection();

  if (!dashboardInfoFromClient || !dashboardInfoFromClient.id) {
    tileCollection.resetDashboardData();
  } else {
    if (dashboardInfoFromClient.data?.tiles?.length) {
      tileCollection.setDashboardId(dashboardInfoFromClient.id);
      const tileParser = new TileStructureParser(
        dashboardInfoFromClient.data,
        false,
      );
      tileCollection.setTiles(tileParser.getTiles());
    } else {
      tileCollection.setDashboardId(dashboardInfoFromClient.id);
      tileCollection.setTiles([]);
    }
  }
};

const compareNodes = (savedStructureWithKeys: any) => {
  if (savedStructureWithKeys.nodeset !== null) {
    new TransferredComparer().setFoundNodeToTransferred();
  }
};

const updateMachineStructure = async (savedStructureNodesObject: any) => {
  saveEditStructurePageTransferData(savedStructureNodesObject);
};

/**
 * Clears both initial and machine structure flat lists from FlatListProvider.
 */
const clearFlatListProviderState = () => {
  FlatListProvider.getInstance().deleteFlatInitialStructureList();
  FlatListProvider.getInstance().deleteFlatMachineStructureList();
};

/**
 * Sets initialStructureNodeset to empty/null state.
 */
const clearInitialStructureNodeset = () => {
  const initialStructureNodeset = getInitialStructureNodeset();
  initialStructureNodeset.setRootNode(null as any);
  initialStructureNodeset.setFlatMap(null as any);
};

/**
 * Sets machineStructureNodeset to empty/null state.
 */
const clearMachineStructureNodeset = () => {
  const machineStructureNodeset = getMachineStructureNodeset();
  machineStructureNodeset.setRootNode(null as any);
  machineStructureNodeset.setFlatMap(null as any);
};

// ============================================================================
// Loading data into singletons and Redux store
// ============================================================================

/**
 * This loads only the machine structure (saved structure by client) and dashboard info.
 * The initial structure is NOT loaded by this function.
 *
 * @returns Promise that resolves when machine structure and dashboards are ready
 */
export const loadMachineStructureAndDashboards = async (
  signal: AbortSignal,
) => {
  const logger = getLogger();

  const { namespace, source_id, connected_via_client } = getSourceInfo();

  logger.debug("loadMachineStructureAndDashboards", {
    message: "Starting quick load of machine structure and dashboards",
  });

  // fetch machine structure and dashboards
  const [savedStructureWithKeys, dashboardsResponse] = await Promise.all([
    apiUseCases.getSavedStructureByClient(connected_via_client, signal),
    apiUseCases.getDashboards(namespace, source_id, signal),
  ]);

  const dashboardInfo = dashboardsResponse?.[0] || null;

  // populate machine structure into singleton
  if (savedStructureWithKeys?.nodeset) {
    const machineStructureRootNode: OurNode =
      writeMachineStructureNodesetRootNodeAsTreeInRedux(
        savedStructureWithKeys.nodeset.root_node,
      );
    writeMachineStructureNodesetRootNodeAsMapInRedux(machineStructureRootNode);
  } else {
    logger.debug("loadMachineStructureAndDashboards", {
      message: "Machine structure nodeset is null or undefined",
    });
    clearMachineStructureNodeset();
  }

  // update dashboard and other settings
  await updateMachineStructure(savedStructureWithKeys);
  setDashboardInfo(dashboardInfo);

  logger.debug("loadMachineStructureAndDashboards", {
    message: "Machine structure quick load complete",
    machineStructureNodeset: getMachineStructureNodeset(),
  });

  return savedStructureWithKeys;
};

/**
 * Loads the full node structure (in the background).
 * does NOT block the UI and can be called without awaiting.
 * components are automatically notified when data arrives.
 *
 * The initialStructureNodeset singleton updates propagate via observers to:
 * - TreeViewForMapStructure (flat array updates)
 * - TreeViewInitialStructure (filter panel state)
 * - useNodesCounterSelectorHook (node counts)
 * - all other components listening to initialStructureNodeset
 * - probably something else that I've missed
 *
 * @param savedStructureWithKeys Optional: previously loaded saved structure for merging names
 */
export const loadInitialStructureAsyncInBackground = async (
  savedStructureWithKeys?: any,
  signal?: AbortSignal,
) => {
  const logger = getLogger();

  // reset flag at the start of each load so components that mount
  // during loading still show the spinner
  _initialStructureLoadComplete = false;

  try {
    const { source_id, auth_info } = getSourceInfo();

    logger.debug("loadInitialStructureAsyncInBackground", {
      message: "Starting background load of initial structure",
    });

    const initialStructure = await apiUseCases.getInitialStructure(
      source_id,
      auth_info,
      signal,
    );

    // process and store initial structure in singleton
    if (initialStructure?.nodeset) {
      const rootNodeOurNode = writeInitialStructureNodesetRootNodeAsTreeInRedux(
        initialStructure.nodeset.root_node,
      );
      writeInitialStructureNodesetRootNodeAsMapInRedux(rootNodeOurNode);
    } else {
      logger.debug("loadInitialStructureAsyncInBackground", {
        message: "Initial structure nodeset is null or undefined",
      });
      clearInitialStructureNodeset();
    }

    // update Redux store with initial structure (with merged machine structure name)
    const mergedStructure = savedStructureWithKeys
      ? writeSavedMachineStructureNameIntoInitialStructure(
          initialStructure,
          savedStructureWithKeys,
        )
      : initialStructure;

    clearFlatListProviderState();
    compareNodes(mergedStructure);
    store.dispatch(updateEditStructurePageInitialData(mergedStructure));

    _initialStructureLoadComplete = true;

    logger.debug("loadInitialStructureAsyncInBackground", {
      message: "Background load of initial structure complete",
      initialStructureNodeset: getInitialStructureNodeset(),
    });
  } catch (error) {
    logger.debug("loadInitialStructureAsyncInBackground", {
      message: "Failed to load initial structure in background",
      error: error instanceof Error ? error.message : String(error),
    });
    // clear state on error to prevent inconsistent UI
    clearInitialStructureNodeset();
    _initialStructureLoadComplete = true;
  }
};
